// chapterPage/IssueFlowPanel.jsx
// Guided coaching flow for a single chapter issue.
import React from 'react';
import { getAuthHeaders, urlMap } from '../../../utils';

const SEVERITY_COLOR = {
  critical: '#b91c1c',
  high: '#d97706',
  medium: '#2563eb',
  low: '#6b7280',
};

const ZPD_LEVEL_LABEL = {
  3: 'HIGH SUPPORT',
  2: 'MED SUPPORT',
  1: 'LOW SUPPORT',
};

// ── Step renderers ────────────────────────────────────────────────────────────

class FlowStepReflection extends React.Component {
  render() {
    const { step, onContinue, submitting } = this.props;

    return (
      <div className="flow-step-body">
        <p className="flow-step-prompt">{step.prompt || step.description}</p>
        <button className="flow-btn flow-btn--primary" onClick={() => onContinue('acknowledged')} disabled={submitting}>
          {submitting ? 'Saving…' : 'Continue →'}
        </button>
      </div>
    );
  }
}

class FlowStepFlashCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { flipped: false };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.step !== this.props.step) {
      this.setState({ flipped: false });
    }
  }

  toggleFlip = () => {
    const { step } = this.props;
    const back = step.back || step.explanation;
    if (!back) return;
    this.setState((prevState) => ({ flipped: !prevState.flipped }));
  };

  render() {
    const { step, onContinue, onHelp, submitting } = this.props;
    const { flipped } = this.state;
    const front = step.front || step.prompt || step.title || step.description;
    const back = step.back || step.explanation;

    return (
      <div className="flow-step-body">
        <div
          className={`flow-flashcard${flipped && back ? ' flipped' : ''}`}
          onClick={this.toggleFlip}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && this.toggleFlip()}
        >
          <div className="flow-flashcard-inner">
            <div className="flow-flashcard-front">{front}</div>
            {back && <div className="flow-flashcard-back">{back}</div>}
          </div>
        </div>
        {back && !flipped && <p className="flow-step-hint">Tap the card to flip</p>}
        <div className="flow-step-actions">
          {(!back || flipped) && (
            <button className="flow-btn flow-btn--primary" onClick={() => onContinue('acknowledged')} disabled={submitting}>
              {submitting ? 'Saving…' : 'Got it →'}
            </button>
          )}
          {typeof onHelp === 'function' && (
            <button className="flow-btn flow-btn--secondary" onClick={() => onHelp(step)} disabled={submitting}>
              Help <span className="seed-price-label">1 fig seed</span>
            </button>
          )}
        </div>
        {typeof onHelp === 'function' && (
          <p className="flow-step-hint">Issue-aware route: POST /api/v1/chat/issue-messages</p>
        )}
      </div>
    );
  }
}

class FlowStepQuestion extends React.Component {
  constructor(props) {
    super(props);
    this.state = { value: '' };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.step !== this.props.step) {
      this.setState({ value: '' });
    }
  }

  render() {
    const { step, onSubmit, submitting } = this.props;
    const { value } = this.state;

    return (
      <div className="flow-step-body">
        <p className="flow-step-prompt">{step.prompt}</p>
        <textarea
          className="flow-step-textarea"
          rows={4}
          placeholder="Your answer…"
          value={value}
          onChange={(e) => this.setState({ value: e.target.value })}
          disabled={submitting}
        />
        <button
          className="flow-btn flow-btn--primary"
          disabled={!value.trim() || submitting}
          onClick={() => onSubmit(value.trim())}
        >
          {submitting ? 'Sending…' : 'Submit →'}
        </button>
      </div>
    );
  }
}

class FlowStepFillBlank extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      values: (props.step.fields || []).map(() => ''),
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.step !== this.props.step) {
      this.setState({
        values: (this.props.step.fields || []).map(() => ''),
      });
    }
  }

  updateValue = (index, value) => {
    this.setState((prevState) => {
      const next = [...prevState.values];
      next[index] = value;
      return { values: next };
    });
  };

  render() {
    const { step, onSubmit, submitting } = this.props;
    const { values } = this.state;
    const template = step.template || step.prompt || '';
    const fields = step.fields || [];
    const parts = template.split('___');

    return (
      <div className="flow-step-body">
        {step.prompt && step.template && <p className="flow-step-prompt">{step.prompt}</p>}
        <div className="flow-fillblank">
          {parts.map((part, i) => (
            <React.Fragment key={i}>
              <span>{part}</span>
              {i < parts.length - 1 && (
                <input
                  className="flow-fillblank-input"
                  type="text"
                  placeholder={fields[i] || '…'}
                  value={values[i] || ''}
                  onChange={(e) => this.updateValue(i, e.target.value)}
                  disabled={submitting}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <button
          className="flow-btn flow-btn--primary"
          disabled={values.some((v) => !v.trim()) || submitting}
          onClick={() => onSubmit(values)}
        >
          {submitting ? 'Sending…' : 'Submit →'}
        </button>
      </div>
    );
  }
}

class FlowStepSubtask extends React.Component {
  constructor(props) {
    super(props);
    this.state = { done: false };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.step !== this.props.step) {
      this.setState({ done: false });
    }
  }

  render() {
    const { step, onContinue, submitting } = this.props;
    const { done } = this.state;

    return (
      <div className="flow-step-body">
        <p className="flow-step-prompt">{step.description || step.title}</p>
        {step.estimatedWords && (
          <p className="flow-step-hint">Estimated: ~{step.estimatedWords} words</p>
        )}
        <label className="flow-subtask-check">
          <input
            type="checkbox"
            checked={done}
            onChange={(e) => this.setState({ done: e.target.checked })}
            disabled={submitting}
          />
          <span>Mark as done</span>
        </label>
        <button
          className="flow-btn flow-btn--primary"
          disabled={!done || submitting}
          onClick={() => onContinue('done')}
        >
          {submitting ? 'Saving…' : 'Continue →'}
        </button>
      </div>
    );
  }
}

class FlowStepChoice extends React.Component {
  render() {
    const { step, onSubmit, submitting } = this.props;
    const options = step.options || [];

    return (
      <div className="flow-step-body">
        <p className="flow-step-prompt">{step.prompt}</p>
        <div className="flow-choices">
          {options.map((opt, i) => {
            const label = typeof opt === 'string' ? opt : (opt.label || opt.value);
            return (
              <button
                key={i}
                className="flow-btn flow-btn--choice"
                disabled={submitting}
                onClick={() => onSubmit(label)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
}

class FlowStepContentEdit extends React.Component {
  constructor(props) {
    super(props);
    this.state = { verifyResult: null };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.step !== this.props.step) {
      this.setState({ verifyResult: null });
    }
  }

  handleVerify = async (skip = false) => {
    const { onVerify } = this.props;
    const result = await onVerify(skip);
    this.setState({ verifyResult: result });
  };

  render() {
    const { step, onOpenInEditor, verifying } = this.props;
    const { verifyResult } = this.state;

    if (verifyResult?.passed) {
      return (
        <div className="flow-step-body">
          <div className="flow-verify-pass">
            <span className="flow-verify-icon">✓</span>
            <p>{verifyResult.llmJudgement || 'Edit verified — great work!'}</p>
          </div>
        </div>
      );
    }

    if (verifyResult && !verifyResult.passed) {
      return (
        <div className="flow-step-body">
          <div className="flow-verify-fail">
            <p className="flow-verify-fail-text">
              {verifyResult.reason === 'no_change'
                ? 'It looks like nothing changed yet. Make the edit in the chapter, then come back.'
                : (verifyResult.llmJudgement || 'The edit needs more work.')}
            </p>
            <div className="flow-step-actions">
              <button className="flow-btn flow-btn--secondary" onClick={() => this.setState({ verifyResult: null })}>
                Try again
              </button>
              <button
                className="flow-btn flow-btn--ghost"
                onClick={() => this.handleVerify(true)}
                disabled={verifying}
              >
                {verifying ? 'Saving…' : 'Skip this check'}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flow-step-body">
        <p className="flow-step-prompt">{step.instruction || step.description}</p>
        {step.targetText && (
          <div className="flow-content-edit-target">
            {step.targetHint && <p className="flow-step-hint">{step.targetHint}</p>}
            <blockquote className="flow-content-edit-quote">{step.targetText}</blockquote>
          </div>
        )}
        <div className="flow-step-actions">
          {typeof onOpenInEditor === 'function' && step.targetText && (
            <button
              className="flow-btn flow-btn--secondary"
              onClick={() => onOpenInEditor(step.targetText)}
            >
              Open in Editor ↗
            </button>
          )}
          <button
            className="flow-btn flow-btn--primary"
            onClick={() => this.handleVerify(false)}
            disabled={verifying}
          >
            {verifying ? 'Checking…' : 'Done — check my work'}
          </button>
        </div>
      </div>
    );
  }
}

// ── Step dispatcher ───────────────────────────────────────────────────────────

class FlowStep extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      submitting: false,
      verifying: false,
    };
  }

  handleSubmit = async (value) => {
    const { onRespond, stepIndex } = this.props;
    this.setState({ submitting: true });
    try {
      await onRespond(stepIndex, value);
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleVerify = async (skip) => {
    const { onVerify, stepIndex } = this.props;
    this.setState({ verifying: true });
    try {
      return await onVerify(stepIndex, skip);
    } finally {
      this.setState({ verifying: false });
    }
  };

  render() {
    const { step, onOpenInEditor, onHelp } = this.props;
    const { submitting, verifying } = this.state;

    switch (step.type) {
      case 'reflection':
        return <FlowStepReflection step={step} onContinue={this.handleSubmit} submitting={submitting} />;
      case 'flash_card':
        return <FlowStepFlashCard step={step} onContinue={this.handleSubmit} onHelp={onHelp} submitting={submitting} />;
      case 'question':
        return <FlowStepQuestion step={step} onSubmit={this.handleSubmit} submitting={submitting} />;
      case 'fill_blank':
        return <FlowStepFillBlank step={step} onSubmit={this.handleSubmit} submitting={submitting} />;
      case 'subtask':
        return <FlowStepSubtask step={step} onContinue={this.handleSubmit} submitting={submitting} />;
      case 'choice':
        return <FlowStepChoice step={step} onSubmit={this.handleSubmit} submitting={submitting} />;
      case 'content_edit':
        return (
          <FlowStepContentEdit
            step={step}
            onVerify={this.handleVerify}
            onOpenInEditor={onOpenInEditor}
            verifying={verifying}
          />
        );
      default:
        return (
          <div className="flow-step-body">
            <p className="flow-step-prompt">{step.prompt || step.description || step.title || 'Step'}</p>
            <button className="flow-btn flow-btn--primary" onClick={() => this.handleSubmit('acknowledged')} disabled={submitting}>
              {submitting ? 'Saving…' : 'Continue →'}
            </button>
          </div>
        );
    }
  }
}

// ── Main Panel ────────────────────────────────────────────────────────────────

export default class IssueFlowPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      flow: null,
      loading: true,
      error: null,
      completing: false,
      helpCollapsed: false,
      viewingStep: 0,
    };

    this.activeStepRef = React.createRef();
    this.isUnmounted = false;
  }

  componentDidMount() {
    this.initFlow();
  }

  componentDidUpdate(prevProps, prevState) {
    const { issue, helpMessages, helpTyping, helpError } = this.props;
    const { helpCollapsed, flow, viewingStep } = this.state;

    if (prevProps.issue.id !== issue.id) {
      this.initFlow();
      return;
    }

    const helpChanged =
      prevProps.helpMessages.length !== helpMessages.length ||
      prevProps.helpTyping !== helpTyping ||
      prevProps.helpError !== helpError;

    if (helpChanged && (helpMessages.length > 0 || helpTyping || helpError) && helpCollapsed) {
      this.setState({ helpCollapsed: false });
      return;
    }

    if (prevState.helpCollapsed !== helpCollapsed && helpCollapsed) {
      this.activeStepRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }

    const prevCurrent = prevState.flow?.current_step ?? 0;
    const current = flow?.current_step ?? 0;
    if (flow && prevCurrent !== current && viewingStep !== current) {
      this.setState({ viewingStep: current });
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  initFlow = async () => {
    const { issue } = this.props;

    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(urlMap.issueFlows, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ issueId: issue.id }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!this.isUnmounted) {
        this.setState({ flow: data, loading: false });
      }
    } catch (e) {
      if (!this.isUnmounted) {
        this.setState({ error: e.message || 'Failed to load flow', loading: false });
      }
    }
  };

  handleRespond = async (stepIndex, value) => {
    const { flow } = this.state;

    const res = await fetch(urlMap.respondToFlowStep(flow.id, stepIndex), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const updated = await res.json();
    this.setState({ flow: updated });
  };

  handleVerify = async (stepIndex, skip) => {
    const { flow } = this.state;

    const res = await fetch(urlMap.verifyFlowStep(flow.id, stepIndex), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(skip ? { skip: true } : {}),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const result = data.verificationResult ?? data;

    if (result.passed && data.flow) {
      this.setState({ flow: data.flow });
    } else if (result.passed) {
      const refreshed = await fetch(urlMap.getIssueFlow(flow.id), {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      });
      if (refreshed.ok) {
        this.setState({ flow: await refreshed.json() });
      }
    }

    return result;
  };

  handleComplete = async (markIssueDone) => {
    const { issue, onIssueResolved } = this.props;
    const { flow } = this.state;

    this.setState({ completing: true });
    try {
      const res = await fetch(urlMap.completeIssueFlow(flow.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ markIssueDone }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      this.setState({ flow: updated });
      if (markIssueDone && onIssueResolved) onIssueResolved(issue.id);
    } finally {
      this.setState({ completing: false });
    }
  };

  handleAbandon = async () => {
    const { onClose } = this.props;
    const { flow } = this.state;

    if (!window.confirm('Abandon this flow? You can start a new one later.')) return;
    await fetch(urlMap.abandonIssueFlow(flow.id), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    onClose();
  };

  handleHelp = (step) => {
    const { onHelp, issue } = this.props;
    if (typeof onHelp === 'function') onHelp({ issue, step });
  };

  renderZpdBadge(step) {
    const level = Number(step?.scaffolding_level);
    if (!level || !ZPD_LEVEL_LABEL[level]) return null;
    const role = step?.zpdRole ? String(step.zpdRole).replace(/_/g, ' ') : null;

    return (
      <span
        className={`flow-zpd-badge flow-zpd-badge--level-${level}`}
        title={role ? `${ZPD_LEVEL_LABEL[level]} support • ${role}` : `${ZPD_LEVEL_LABEL[level]} support`}
      >
        {ZPD_LEVEL_LABEL[level]}
      </span>
    );
  }

  renderLoading() {
    const { onClose } = this.props;

    return (
      <div className="issue-flow-panel">
        <div className="issue-flow-header">
          <button className="flow-back-btn" onClick={onClose}>← Back</button>
        </div>
        <div className="issue-flow-loading">Preparing your coaching session…</div>
      </div>
    );
  }

  renderError() {
    const { onClose } = this.props;
    const { error } = this.state;

    return (
      <div className="issue-flow-panel">
        <div className="issue-flow-header">
          <button className="flow-back-btn" onClick={onClose}>← Back</button>
        </div>
        <div className="issue-flow-error">⚠ {error || 'Flow not found'}</div>
      </div>
    );
  }

  render() {
    const {
      issue,
      onClose,
      onOpenInEditor,
      helpMessages = [],
      helpTyping = false,
      helpError = null,
    } = this.props;

    const {
      flow,
      loading,
      error,
      completing,
      helpCollapsed,
      viewingStep,
    } = this.state;

    if (loading) {
      return this.renderLoading();
    }

    if (error || !flow) {
      return this.renderError();
    }

    const steps = flow.steps || [];
    const current = flow.current_step ?? 0;
    const isCompleted = flow.status === 'completed';
    const allDone = current >= steps.length && steps.length > 0;
    const progress = steps.length > 0 ? Math.min(current / steps.length, 1) : 0;
    const currentStep = steps[current];

    const severityColor = SEVERITY_COLOR[issue.severity] || SEVERITY_COLOR.medium;
    const visibleHelpMessages = helpMessages.filter((message) => message.from !== 'me').slice(-1);

    const maxNav = isCompleted ? steps.length - 1 : Math.min(current, steps.length - 1);
    const isViewingCurrent = viewingStep === current;
    const displayStep = steps[viewingStep];

    return (
      <div className="issue-flow-panel">
        <div className="issue-flow-header">
          <button className="flow-back-btn" onClick={onClose}>← Back</button>
          <div className="issue-flow-title-row">
            <span className="issue-flow-severity-dot" style={{ background: severityColor }} />
            <span className="issue-flow-title">{issue.title}</span>
          </div>
          {!isCompleted && (
            <button className="flow-abandon-btn" onClick={this.handleAbandon} title="Abandon flow">✕</button>
          )}
        </div>

        <div className="issue-flow-progress-track">
          <div className="issue-flow-progress-fill" style={{ width: `${progress * 100}%` }} />
        </div>

        <div className="issue-flow-nav">
          <button
            className="flow-nav-btn"
            aria-label="Previous step"
            disabled={viewingStep === 0}
            onClick={() => this.setState((prevState) => ({ viewingStep: prevState.viewingStep - 1 }))}
          >←</button>
          <span className="issue-flow-step-label">
            {isCompleted
              ? `Step ${viewingStep + 1} of ${steps.length} · Complete`
              : steps.length === 0
              ? 'Loading steps…'
              : `Step ${viewingStep + 1} of ${steps.length}`}
          </span>
          <button
            className="flow-nav-btn"
            aria-label="Next step"
            disabled={viewingStep >= maxNav}
            onClick={() => this.setState((prevState) => ({ viewingStep: prevState.viewingStep + 1 }))}
          >→</button>
          {!isViewingCurrent && !isCompleted && (
            <button
              className="flow-nav-btn flow-nav-btn--jump"
              onClick={() => this.setState({ viewingStep: current })}
            >Current ↓</button>
          )}
        </div>

        <div className="issue-flow-body">
          {!isViewingCurrent && displayStep && (
            <div className="flow-active-step flow-active-step--past" ref={this.activeStepRef}>
              <div className="flow-active-step-header">
                <span className="flow-active-step-num">Step {viewingStep + 1}</span>
                <span className="flow-active-step-type">{displayStep.type?.replace(/_/g, ' ')}</span>
                {this.renderZpdBadge(displayStep)}
                <span className="flow-completed-check">✓ Done</span>
              </div>
              {(displayStep.label || displayStep.title) && (
                <div className="flow-active-step-title">{displayStep.label || displayStep.title}</div>
              )}
              <div className="flow-step-body">
                <p className="flow-step-prompt">
                  {displayStep.front || displayStep.prompt || displayStep.instruction || displayStep.description || `Step ${viewingStep + 1}`}
                </p>
                {displayStep.back && (
                  <p className="flow-step-hint">{displayStep.back}</p>
                )}
              </div>
            </div>
          )}

          {isViewingCurrent && !isCompleted && !allDone && currentStep && (
            <div className="flow-active-step" ref={this.activeStepRef}>
              <div className="flow-active-step-header">
                <span className="flow-active-step-num">Step {current + 1}</span>
                <span className="flow-active-step-type">{currentStep.type?.replace(/_/g, ' ')}</span>
                {this.renderZpdBadge(currentStep)}
              </div>
              {(currentStep.label || currentStep.title) && (
                <div className="flow-active-step-title">{currentStep.label || currentStep.title}</div>
              )}
              <FlowStep
                step={currentStep}
                stepIndex={current}
                onRespond={this.handleRespond}
                onVerify={this.handleVerify}
                onOpenInEditor={onOpenInEditor}
                onHelp={this.handleHelp}
              />
            </div>
          )}

          {(visibleHelpMessages.length > 0 || helpTyping || helpError) && (
            <div className="flow-help-panel">
              <div className="flow-help-header-row">
                <div className="flow-help-header">Coach</div>
                <button
                  type="button"
                  className="flow-help-toggle"
                  onClick={() => this.setState((prevState) => ({ helpCollapsed: !prevState.helpCollapsed }))}
                >
                  {helpCollapsed ? 'Show' : 'Hide'}
                </button>
              </div>
              {!helpCollapsed && (
                <div className="flow-help-messages">
                  {helpError && <div className="flow-help-bubble flow-help-bubble--them">⚠ {helpError}</div>}
                  {visibleHelpMessages.map((message) => (
                    <div
                      key={message.id}
                      className={`flow-help-bubble ${message.from === 'me' ? 'flow-help-bubble--me' : 'flow-help-bubble--them'}`}
                    >
                      {message.text}
                    </div>
                  ))}
                  {helpTyping && <div className="flow-help-bubble flow-help-bubble--them">Thinking…</div>}
                </div>
              )}
            </div>
          )}

          {!isCompleted && allDone && (
            <div className="flow-completion">
              <div className="flow-completion-icon">Complete</div>
              <p className="flow-completion-msg">You've worked through all the steps!</p>
              <button
                className="flow-btn flow-btn--primary"
                onClick={() => this.handleComplete(true)}
                disabled={completing}
              >
                {completing ? 'Saving…' : 'Mark issue resolved'}
              </button>
              <button
                className="flow-btn flow-btn--ghost"
                onClick={() => this.handleComplete(false)}
                disabled={completing}
              >
                Close without resolving
              </button>
            </div>
          )}

          {isCompleted && (
            <div className="flow-completion">
              <div className="flow-completion-icon">Complete</div>
              <p className="flow-completion-msg">This issue flow is complete.</p>
              <button className="flow-btn flow-btn--ghost" onClick={onClose}>Close</button>
            </div>
          )}
        </div>
      </div>
    );
  }
}
